import { I18n } from "@assets/strings";
import { identity } from "lodash";
import React from "react";
import { useLocation } from "react-router";
import { Link } from "react-router";
import styled from "styled-components";
import logoOmat from "@assets/images/logo_omat.png";
import { config } from "@core/config/config";
import { Environment } from "@core/config/environment";
import { BrowserLocalStorage } from "@core/localStorage/storage";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { Application } from "@domain/application/application";
import { ApplicationVoteDto } from "@domain/application/dtos";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Person } from "@domain/user/user";
import { useAsync, useIsMobile, useVoteOnLocalStorage } from "@utils/hooks";
import { PrimaryButton, SecondaryTouchButton } from "@ui/components/buttons/primaryButton";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { LikeIcon, ShareIcone } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { LinkExternal } from "@ui/components/linkExternal";
import { TooshareVideoPlayer } from "@ui/components/media/tooshareVideoPlayer";
import { useModalTools } from "@ui/modals/modalContext";
import { ShareProfileModale } from "@ui/pages/application/applicationEditorPage/shareProfile";
import { theme } from "@ui/styles/theme";
import { desktop, gap, mobile, smallMobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";

interface ApplicationStoryThumbnailProps extends React.HTMLAttributes<HTMLDivElement> {
	application: Application;
	person: Person;
	voterId?: Person;
	isFeed?: boolean;
}

//TODO optimize code

// function checkDate(votedDate: Date): boolean {
// 	return !(new Date().getHours() - new Date(votedDate).getHours() >= 24);
// }

export const ApplicationStoryThumbnail: React.FC<ApplicationStoryThumbnailProps> = ({
	application,
	person,
	isFeed,
	...htmlProps
}) => {
	const currentProfile = useCurrentProfile();
	const { applicationService } = useServices();
	const location = useLocation();
	const { createModal } = useModalTools();

	const votesStorage = new BrowserLocalStorage<ApplicationVoteDto | null>("votes", identity, identity, "local");
	const votesOnLocalStorage = votesStorage?.getItem();
	const isMobile = useIsMobile();

	//const { result: application, loading } = useApplicationByUser(profile.id, currentProfile?.id);

	const { hasVoted, voteCount, lastVote, vote } = useVoteOnLocalStorage(
		person.slug ?? location.pathname.slice(9).replaceAll("/", ""),
		currentProfile,
		application
	);

	if (!applicationService) {
		return null;
	}

	const { run } = useAsync(async (applicationId: string, key?: string): Promise<ApplicationVoteDto> => {
		vote();
		createToast(I18n.t("vote.success_toast.title"), I18n.t("vote.success_toast.message"), "success");
		const result = await applicationService.vote(applicationId, key);

		if (!votesOnLocalStorage) {
			votesStorage.setItem(result);
		} else {
			votesStorage.setItem({ ...votesOnLocalStorage, ...result });
		}

		return result;
	}, []);

	return (
		<Container {...htmlProps}>
			<StyledCanonicalLink>
				<Link to={Path.Profile(person)}>{`${Environment.APP_BASE_URL}${Path.Profile(person)}`}</Link>
			</StyledCanonicalLink>

			<Content>
				<StyledOmatHeader>
					<StyledBlocTalent>
						<LinkExternal to="https://onemillionafricantalents.com">
							<img src={logoOmat} width="95px" height="29px" />
						</LinkExternal>
						<Link to="httonemillionafricantalents.com"></Link>
						<TalentTitle>{I18n.t("vote.baseline.label")}</TalentTitle>
					</StyledBlocTalent>
					{isMobile && (
						<StyledBlocCounter>
							<CounterValue>
								{I18n.t(voteCount > 1 ? "vote.counter.label" : "vote.counter.single.label", {
									total: voteCount,
								})}
							</CounterValue>
						</StyledBlocCounter>
					)}
				</StyledOmatHeader>
				{!!application.videoId && <TooshareVideoPlayer asset={application.videoId} />}
				<BlocBtn>
					<StyledButton>
						<ShareButton
							onClick={() =>
								createModal((close) => (
									<ShareProfileModale
										canonicalLink={`${Environment.APP_BASE_URL}${Path.Profile(person)}`}
										close={close}
										person={person}
									/>
								))
							}
							disabled={!config.features.program}
						>
							<ShareIcone />
							<StyledLabel>{I18n.t("vote.button.share.label")}</StyledLabel>
						</ShareButton>
						<VotedButton
							onClick={() => {
								if (!currentProfile) {
									if (!lastVote) {
										run(application.id);
										return;
									}
									if (lastVote) {
										run(application.id, lastVote.key);
										return;
									}
								}
								run(application.id);
							}}
							disabled={!config.features.program}
						>
							<LikeIcon color={!hasVoted ? theme.colors.white : "#5D5D5D"} />
							<StyledLabel active={hasVoted}>{I18n.t("vote.button.vote.label")}</StyledLabel>
						</VotedButton>
					</StyledButton>
					{!isMobile && (
						<CounterValue>
							{I18n.t(voteCount > 1 ? "vote.counter.label" : "vote.counter.single.label", {
								total: voteCount,
							})}
						</CounterValue>
					)}
				</BlocBtn>
			</Content>
		</Container>
	);
};

const Container = styled.div`
	${gap(16)};
`;

const StyledOmatHeader = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	margin-top: 20px;
	margin-left: 20px;
	margin-right: 20px;
`;
const StyledBlocTalent = styled.div`
	display: flex;
	flex-direction: column;
`;

const TalentTitle = styled.div`
	display: flex;
	flex-direction: column;
	font-family: Sora;
	font-style: normal;
	font-weight: 700;
	font-size: 10px;
	line-height: 25px;
	color: #206af8;
`;

const CounterValue = styled.div`
	font-family: Sora;
	font-style: normal;
	font-weight: 700;
	font-size: 20px;
	line-height: 25px;
	color: #000000;
`;

const Content = styled(RoundedBlock)`
	${gap(15)};
`;

const VotedButton = styled(PrimaryButton)`
	${row(10, "space-between", "center")}
	svg {
		width: 20px;
		height: 20px;
	}
	&:disabled {
		color: red !important;
	}
	@media ${mobile} {
		padding: 12px 30px !important ;
	}
	@media ${smallMobile} {
		padding: 12px 10px !important ;
	}
`;

const BlocBtn = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	gap: 10px;
	margin: 15px 5px;
`;

const StyledLabel = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>`
	${({ theme }) => theme.textStyles.title}
	font-size: 14px !important;
	color: ${({ active, theme }) => (!active ? theme.colors.white : "#5D5D5D")} !important;
`;
const StyledCanonicalLink = styled.div`
	cursor: pointer;
	color: ${({ theme }) => theme.colors.black};
	@media ${smallMobile} {
		font-size: 10px !important;
	}
`;

const ShareButton = styled(SecondaryTouchButton)`
	${row(10, "space-between", "center")}
	svg {
		width: 20px;
		height: 20px;
	}
	@media ${mobile} {
		padding: 12px 30px !important ;
	}
	@media ${smallMobile} {
		padding: 12px 10px !important ;
	}
`;

const StyledButton = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;

	@media ${mobile} {
		width: 100%;
	}
	@media ${desktop} {
		gap: 30px;
	}
`;

const StyledBlocCounter = styled.div`
	display: flex;
	flex-direction: row;
`;
